import { ref } from 'vue';
import wx from 'weixin-js-sdk';
import { getApi } from '../../init';

export function useMobilePay() {
  const { aiadOrderApi, tcWechatApi, tcAlipayApi } = getApi();

  const loading = ref(false);
  const payInfo = ref();
  const orderInfo = ref();

  async function createOrder(orderChannel: string, payCode: string) {
    orderInfo.value = await aiadOrderApi.createOrder({
      code: payCode,
      orderChannel,
    });
  }

  async function initWx(options: {
    orderChannel: string;
    payCode: string;
    wxConfig: { appid: string; openid: string };
  }) {
    const { orderChannel, payCode, wxConfig } = options;
    const { openid, appid } = wxConfig;
    loading.value = true;

    // 获取微信sdk配置，创建订单
    const result = await Promise.all([
      tcWechatApi.getWechatSdkInitParams({
        url: location.href,
      }),
      createOrder(orderChannel, payCode),
    ]);
    const { nonceStr, signature, timestamp } = result[0];

    wx.config({
      // debug: true, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
      appId: appid, // 必填，公众号的唯一标识
      timestamp, // 必填，生成签名的时间戳
      nonceStr, // 必填，生成签名的随机串
      signature, // 必填，签名
      jsApiList: ['chooseWXPay'], // 必填，需要使用的JS接口列表
    });

    payInfo.value = await tcWechatApi.createOrder({
      openId: openid,
      tradeNo: orderInfo.value.tradeNo,
    });
    loading.value = false;
  }

  function handleWxPay() {
    wx.ready(() => {
      wx.chooseWXPay({
        timestamp: payInfo.value.timeStamp,
        nonceStr: payInfo.value.nonceStr,
        package: payInfo.value.packageVal,
        signType: payInfo.value.signType,
        paySign: payInfo.value.paySign,
        success: () => {},
        fail: (e) => {
          console.log(e);
        },
      });
    });

    wx.error(function (res) {
      console.log('error', res);
    });
  }

  async function initWxH5(options: { orderChannel: string; payCode: string }) {
    const { orderChannel, payCode } = options;
    await createOrder(orderChannel, payCode);
  }

  async function handleWxH5Pay(options: {
    payCode: string;
    orderChannel: string;
    wxConfig: { payRedirectUri: string; paySuccessRedirectUri: string };
  }) {
    const { payCode, orderChannel, wxConfig } = options;
    const { h5Url } = await tcWechatApi.createH5Order({
      tradeNo: orderInfo.value.tradeNo,
    });

    const redirectUrl = `${
      wxConfig.payRedirectUri
    }?payCode=${payCode}&orderChannel=${orderChannel}&redirectUri=${
      wxConfig.paySuccessRedirectUri || location.href
    }`;
    location.replace(`${h5Url}&redirect_url=${encodeURIComponent(redirectUrl)}`);
  }

  async function initZfb(options: {
    orderChannel: string;
    payCode: string;
    zfbConfig: {
      payRedirectUri: string;
    };
  }) {
    const { orderChannel, payCode, zfbConfig } = options;
    loading.value = true;
    await createOrder(orderChannel, payCode);

    payInfo.value = await tcAlipayApi.createOrder({
      tradeNo: orderInfo.value.tradeNo,
      returnUrl: `${
        zfbConfig.payRedirectUri
      }?payCode=${payCode}&orderChannel=${orderChannel}&redirectUri=${encodeURIComponent(
        location.href,
      )}`,
    });

    loading.value = false;
  }

  function handleZfbPay() {
    const div = document.createElement('div');
    div.innerHTML = payInfo.value;
    document.body.appendChild(div);
    const form = div.firstChild;
    form!.submit();
  }

  function getOrderEnv() {
    if (/MicroMessenger/.test(window.navigator.userAgent)) {
      return 'wechat';
    } else if (/AlipayClient/.test(window.navigator.userAgent)) {
      return 'alipay';
    } else {
      return '';
    }
  }

  async function pay({ orderChannel, payCode, wxConfig, zfbConfig }) {
    // 判断当前支付环境
    const orderEnv = getOrderEnv();

    // 支付方式为支付宝
    if (orderChannel === 'alipay') {
      await initZfb({ orderChannel, payCode, zfbConfig });
      await handleZfbPay();
    }

    // 支付方式为微信
    else if (orderChannel === 'wechat') {
      // 微信环境下使用wx-jssdk
      if (orderEnv === 'wechat') {
        await initWx({ orderChannel, payCode, wxConfig });
        await handleWxPay();
      } else {
        // 非微信环境下使用deeplink
        await initWxH5({ orderChannel, payCode });
        await handleWxH5Pay({ orderChannel, payCode, wxConfig });
      }
    }
  }

  return {
    pay,
  };
}
